import { useEffect, useRef, useState } from "react";
import Icon from "@mdi/react";
import { mdiDownload, mdiFileImportOutline, mdiTrashCanOutline } from "@mdi/js";
import { useHistory } from "react-router-dom";
import Papa from "papaparse";
import {
  ToastType,
  useToast,
} from "../../../../context_providers/toast/toast_ctx";
import LoadingWidget from "../../../../context_providers/modal/loader";
import {
  ModalType,
  useModal,
} from "../../../../context_providers/modal/modal_context";
import RequiredMark from "../../../../ui/new_components/common/required_mark";
import WarehouseSearchField from "../../warehouse/warehouse_search_field";
import NewItemSearchByIdField from "../../items/item_search_by_ids";
import { WarehouseModel } from "../../models/warehouse_model";
import { ItemModelV2 } from "../../models/item_model";
import {
  BulkStockRow,
  BulkUploadType,
  PreviewResult,
  PreviewStockActionApi,
  RaiseStockActionApi,
  StockActionRequestPayload,
  StockRequestAction,
  UpdateStockActionRequestApi,
} from "./bulk_stock_upload_repo";
import {
  GetTransformableItemsApi,
  GetTransformsForItemApi,
  TransformMasterModel,
} from "../../transform/transform_master_repo";
import StockActionPreviewPanel from "./StockActionPreviewPanel";
import { urls } from "../../../../utils/urls";

export interface StockRequestEditInitial {
  warehouse?: WarehouseModel | null;
  item?: ItemModelV2 | null;
  toItem?: ItemModelV2 | null;
  remarks?: string;
  fileName?: string;
  fileUrl?: string;
  deviceIds?: string[];
  rows?: BulkStockRow[];
  // Item Code Transfer (transform master) re-hydration when editing.
  sourceItemCode?: string;
  transformId?: string;
}

interface BulkStockUploadFormProps {
  type: BulkUploadType;
  fileLabel?: string;
  // edit mode: editing an existing rejected/pending request (decision #3)
  editRequestId?: string;
  initial?: StockRequestEditInitial;
}

export default function BulkStockUploadForm({
  type,
  fileLabel,
  editRequestId,
  initial,
}: BulkStockUploadFormProps): JSX.Element {
  const history = useHistory();
  const { showToast } = useToast();
  const { showModal, onClose } = useModal();
  const isEdit = !!editRequestId;

  const showErrorModal = (message: string, title = "Upload Failed") => {
    showModal({
      title,
      type: ModalType.modal,
      container: (id) => (
        <div id="error_modal" className="p-2 max-w-md">
          <p className="text-sm text-gray-700 whitespace-pre-line">{message}</p>
          <div className="mt-4 flex justify-end">
            <button
              id="modal_close_btn"
              onClick={() => onClose(id)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
            >
              Close
            </button>
          </div>
        </div>
      ),
    });
  };
  const [warehouse, setWarehouse] = useState<WarehouseModel | null>(
    initial?.warehouse ?? null,
  );
  const [selectedItem, setSelectedItem] = useState<ItemModelV2 | null>(
    initial?.item ?? null,
  );
  const [remarks, setRemarks] = useState<string>(initial?.remarks ?? "");
  const [fileName, setFileName] = useState<string>(initial?.fileName ?? "");
  const [rows, setRows] = useState<BulkStockRow[]>([]);
  const [file, setFile] = useState<File | null>(null);
  const [deviceIds, setDeviceIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Item Code Transfer is driven by the transform master: the source item can
  // only be one that participates in a transform, and the target items are
  // fetched from the selected transform (not free-typed).
  const [transformableItems, setTransformableItems] = useState<
    { item_code: string; item_name: string }[]
  >([]);
  const [sourceItemCode, setSourceItemCode] = useState<string>(
    initial?.sourceItemCode ?? "",
  );
  const [transforms, setTransforms] = useState<TransformMasterModel[]>([]);
  const [selectedTransformId, setSelectedTransformId] = useState<string>(
    initial?.transformId ?? "",
  );

  // any change to the inputs invalidates a computed preview
  useEffect(() => {
    setPreview(null);
  }, [
    warehouse,
    selectedItem,
    sourceItemCode,
    selectedTransformId,
    remarks,
    file,
  ]);

  const isTransfer = type === "ITEM_CODE_TRANSFER";
  const isRM = type === "RAW_MATERIAL_UPLOAD";
  const isDeviceIdFile = !isRM;
  // item_code is required for FG flows and transfer; RM derives it per CSV row.
  const needsItem =
    type === "FG_STOCK_IN" || type === "FG_STOCK_OUT" || isTransfer;

  useEffect(() => {
    if (!isTransfer) return;
    (async () => {
      const res = await GetTransformableItemsApi();
      if (res.success) setTransformableItems(res.data ?? []);
      else showToast({ type: ToastType.error, text: res.error });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTransfer]);

  // Editing an existing transfer request: load the transforms for the stored
  // source item so the transformation dropdown re-populates.
  useEffect(() => {
    if (!isTransfer || !initial?.sourceItemCode) return;
    (async () => {
      const res = await GetTransformsForItemApi(initial.sourceItemCode!);
      if (res.success) setTransforms(res.data ?? []);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTransfer, initial?.sourceItemCode]);

  const onSourceItemChange = async (code: string) => {
    setSourceItemCode(code);
    setSelectedTransformId("");
    setTransforms([]);
    if (!code) return;
    const res = await GetTransformsForItemApi(code);
    if (res.success) setTransforms(res.data ?? []);
    else showToast({ type: ToastType.error, text: res.error });
  };

  const transformLabel = (t: TransformMasterModel) =>
    `${(t.inputs || []).map((i) => i.item_code).join(" + ")} → ${(
      t.outputs || []
    )
      .map((o) => o.item_code)
      .join(" + ")}`;

  const selectedTransform =
    transforms.find((t) => t.id === selectedTransformId) || null;

  const downloadSampleCSV = () => {
    const csvContent = isRM ? "Item Code,Qty\nITEM001,10" : "Device ID\n123456";
    const fileName = isRM
      ? "rm_stock_upload_sample.csv"
      : isTransfer
        ? "item_code_transfer_sample.csv"
        : "device_stock_sample.csv";
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", fileName);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError("");
    const file = e.target.files?.[0];
    if (!file) return;

    if (isDeviceIdFile) {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        transformHeader: (header) => header.trim(),
        complete: (results) => {
          const data = results.data as any[];
          const ids: string[] = [];

          data.forEach((row) => {
            const id = row["Device ID"]?.toString().trim();
            if (id) ids.push(id);
          });

          if (ids.length === 0) {
            showToast({
              type: ToastType.warning,
              text: "No valid rows found. Header should be 'Device ID'",
            });
            return;
          }

          setFile(file);
          setDeviceIds(ids);
          setFileName(file.name);
          showToast({
            type: ToastType.success,
            text: `${ids.length} device(s) loaded from file`,
          });
        },
        error: (err) => {
          showToast({
            type: ToastType.error,
            text: "Error parsing CSV: " + err.message,
          });
        },
      });
      return;
    }

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (header) => header.trim(),
      complete: (results) => {
        const data = results.data as any[];
        const parsed: BulkStockRow[] = [];

        data.forEach((row) => {
          const item_code = row["Item Code"]?.toString().trim();
          const quantity = Number(row["Qty"]);
          if (item_code && !isNaN(quantity) && quantity >= 0) {
            parsed.push({ item_code, quantity });
          }
        });

        if (parsed.length === 0) {
          showToast({
            type: ToastType.warning,
            text: "No valid rows found. Headers should be 'Item Code, Qty'",
          });
          return;
        }

        setFile(file);
        setFileName(file.name);
        setRows(parsed);
        showToast({
          type: ToastType.success,
          text: `${parsed.length} rows loaded from file`,
        });
      },
      error: (err) => {
        showToast({
          type: ToastType.error,
          text: "Error parsing CSV: " + err.message,
        });
      },
    });
  };

  const clearFile = () => {
    setFileName("");
    setRows([]);
    setFile(null);
    setDeviceIds([]);
    setError("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // Pure validation for all maker/checker flows (FG in/out, RM, and the
  // transform-master driven Item Code Transfer). Returns an error message or
  // null.
  const validateInputs = (): string | null => {
    if (!warehouse) return "Please select a warehouse";
    if (isTransfer) {
      if (!sourceItemCode) return "Please select an item";
      if (!selectedTransform) return "Please select a transformation";
    } else {
      if (needsItem && !selectedItem) return "Please select an item";
    }
    if (!isEdit && !file) return "Please upload a file";
    if (!remarks.trim()) return "Please enter remarks";
    return null;
  };

  const buildPayload = (): StockActionRequestPayload => ({
    action:
      StockRequestAction[
        type as Exclude<BulkUploadType, "SCAN_CODE_DEVICES_FETCH">
      ],
    file: file ?? undefined,
    warehouse_id: warehouse!.id,
    // For a transform the source code doubles as item_code so the request list
    // shows it; the backend plans/commits from transform_id + source_item_code.
    item_code: isTransfer
      ? sourceItemCode
      : needsItem
        ? selectedItem?.item_code
        : undefined,
    transform_id: isTransfer ? selectedTransform?.id : undefined,
    source_item_code: isTransfer ? sourceItemCode : undefined,
    remarks: remarks.trim(),
  });

  const showBlockingIssuesModal = (preview: PreviewResult) => {
    const lines = preview.blocking_errors.map((e) => `• ${e.message}`);
    const msg = lines.length
      ? lines.join("\n")
      : "This file has blocking issues and cannot be raised.";
    setError(preview.summary || "Request has blocking issues");
    showErrorModal(msg, "Cannot raise — resolve these issues");
  };

  const handlePreview = async () => {
    const err = validateInputs();
    if (err) return setError(err);
    setError("");
    try {
      setLoading(true);
      const res = await PreviewStockActionApi(buildPayload());
      if (res?.success && res.preview) {
        setPreview(res.preview);
        if (!res.preview.ok) showBlockingIssuesModal(res.preview);
      } else {
        const msg = res?.message || res?.error || "Failed to compute preview";
        setError(msg);
        showErrorModal(msg);
      }
    } catch (e: any) {
      setError(e.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleRaise = async () => {
    const err = validateInputs();
    if (err) return setError(err);
    setError("");
    try {
      setLoading(true);
      const res = await RaiseStockActionApi(buildPayload());
      if (res?.success) {
        showToast({
          type: ToastType.success,
          text: res?.message || "Request raised for approval",
        });
        history.push(urls.stock_action_requests);
        return;
      }
      // backend refused (e.g. preview no longer ok) — surface its preview
      if (res?.preview) setPreview(res.preview);
      const msg = res?.message || res?.error || "Failed to raise request";
      setError(msg);
      showErrorModal(msg);
    } catch (e: any) {
      setError(e.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    const err = validateInputs();
    if (err) return setError(err);
    setError("");
    try {
      setLoading(true);
      const res = await UpdateStockActionRequestApi(
        editRequestId!,
        buildPayload(),
      );
      if (res?.success) {
        showToast({
          type: ToastType.success,
          text: res?.message || "Request updated & resubmitted",
        });
        history.push(urls.stock_action_requests);
        return;
      }
      if (res?.preview) setPreview(res.preview);
      const msg = res?.message || res?.error || "Failed to update request";
      setError(msg);
      showErrorModal(msg);
    } catch (e: any) {
      setError(e.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div id="form_container">
      <div className="p-6 space-y-6">
        {/* Warehouse + Item */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div id="warehouse_field">
            <label className="text-sm font-medium">
              Warehouse <RequiredMark />
            </label>
            <WarehouseSearchField
              value={warehouse || undefined}
              placeholder="Select warehouse"
              onSelect={(w) => setWarehouse(w)}
            />
          </div>

          {needsItem && !isTransfer && (
            <div id="item_field">
              <label className="text-sm font-medium">
                {isTransfer ? "Transfer From Item" : "Item"} <RequiredMark />
              </label>
              <NewItemSearchByIdField
                disabled={!warehouse}
                value={selectedItem || ({} as ItemModelV2)}
                placeholder={
                  isTransfer ? "Search item to transfer from" : "Search item"
                }
                onSelect={setSelectedItem}
              />
              {selectedItem?.item_code && (
                <p className="text-xs text-gray-500 mt-1">
                  Item Code: {selectedItem.item_code}
                </p>
              )}
            </div>
          )}

          {isTransfer && (
            <div>
              <label className="text-sm font-medium">
                Item <RequiredMark />
              </label>
              <select
                id="source_item_select"
                disabled={!warehouse}
                value={sourceItemCode}
                onChange={(e) => onSourceItemChange(e.target.value)}
                className="w-full h-8 border rounded-md px-2 disabled:opacity-50"
              >
                <option value="">Select item</option>
                {transformableItems.map((it) => (
                  <option key={it.item_code} value={it.item_code}>
                    {it.item_code} — {it.item_name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {isTransfer && !!sourceItemCode && (
            <div>
              <label className="text-sm font-medium">
                Convert To <RequiredMark />
              </label>
              <select
                id="transform_select"
                value={selectedTransformId}
                onChange={(e) => setSelectedTransformId(e.target.value)}
                className="w-full h-8 border rounded-md px-2"
              >
                <option value="">Select conversion</option>
                {transforms.map((t) => (
                  <option key={t.id} value={t.id}>
                    {transformLabel(t)}
                  </option>
                ))}
              </select>
              {transforms.length === 0 && (
                <p className="text-xs text-gray-500 mt-1">
                  No conversions available for this item.
                </p>
              )}
            </div>
          )}
        </div>

        {isTransfer && selectedTransform && (
          <div className="border rounded-md p-3 bg-gray-50 space-y-2 text-sm">
            <div>
              <span className="font-medium">Consumes:</span>{" "}
              {(selectedTransform.inputs.some(
                (i) => i.item_code === sourceItemCode,
              )
                ? selectedTransform.inputs
                : selectedTransform.outputs
              )
                .map((i) => `${i.item_code} ×${i.qty}`)
                .join(", ")}
            </div>
            <div>
              <span className="font-medium">Produces:</span>{" "}
              {(selectedTransform.inputs.some(
                (i) => i.item_code === sourceItemCode,
              )
                ? selectedTransform.outputs
                : selectedTransform.inputs
              )
                .map((o) => `${o.item_code} ×${o.qty}`)
                .join(", ")}
            </div>
            <p className="text-xs text-gray-500">
              Upload the device IDs of the selected item; quantities for the
              other lines scale by the number of devices.
            </p>
          </div>
        )}

        {/* Remarks */}
        <div>
          <label className="text-sm font-medium">
            Remarks <RequiredMark />
          </label>
          <textarea
            id="remarks_input"
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            placeholder="Add remarks"
            rows={2}
            className="w-full border rounded-md px-3 py-2"
          />
        </div>

        {/* File Upload */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">
              {fileLabel || "Upload File"}
            </h2>
            <button
              id="download_sample_btn"
              onClick={downloadSampleCSV}
              className="text-gray-500 text-xs flex items-center gap-1 hover:text-blue-600 underline"
            >
              <Icon path={mdiDownload} size={0.5} />
              Download Sample CSV
            </button>
          </div>

          <div className="relative">
            <input
              id="csv_file_input"
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileUpload}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              disabled={loading}
            />
            <button
              id="upload_btn"
              disabled={loading}
              className="w-full border-2 border-dashed border-blue-400 text-blue-600 h-24 rounded-md font-medium flex flex-col items-center justify-center gap-2 hover:bg-blue-50 transition-all disabled:opacity-50"
            >
              <Icon path={mdiFileImportOutline} size={1} />
              {file
                ? fileName
                : isRM
                  ? "Click to upload CSV (Item Code, Qty)"
                  : "Click to upload CSV (Device ID)"}
            </button>
          </div>

          {isEdit && !file && initial?.fileName && (
            <div id="prev_file_section" className="space-y-2">
              <p className="text-sm font-medium text-gray-700">
                Previously uploaded file
              </p>
              <div className="flex items-center justify-between border rounded-md px-4 py-3 bg-gray-50">
                <div className="flex items-center gap-2 text-sm min-w-0">
                  <Icon path={mdiFileImportOutline} size={0.8} />
                  {initial.fileUrl ? (
                    <a
                      id="prev_file_link"
                      href={initial.fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline truncate"
                    >
                      {initial.fileName}
                    </a>
                  ) : (
                    <span className="truncate">{initial.fileName}</span>
                  )}
                </div>
                {initial.fileUrl && (
                  <a
                    id="prev_file_download_link"
                    href={initial.fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 text-xs flex items-center gap-1 hover:text-blue-700 underline shrink-0"
                  >
                    <Icon path={mdiDownload} size={0.6} />
                    Download
                  </a>
                )}
              </div>
              {isRM && (initial.rows?.length ?? 0) > 0 && (
                <div id="prev_rm_rows_table" className="max-h-64 overflow-auto border rounded-md">
                  <table className="min-w-full text-sm divide-y divide-gray-200">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-4 py-2 text-left font-medium">#</th>
                        <th className="px-4 py-2 text-left font-medium">
                          Item Code
                        </th>
                        <th className="px-4 py-2 text-right font-medium">
                          Qty
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {initial.rows!.map((row, index) => (
                        <tr id={`prev_rm_row_${index}`} key={index}>
                          <td className="px-4 py-2">{index + 1}</td>
                          <td className="px-4 py-2">{row.item_code}</td>
                          <td className="px-4 py-2 text-right">
                            {row.quantity}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {isDeviceIdFile && (initial.deviceIds?.length ?? 0) > 0 && (
                <div id="prev_device_table" className="max-h-64 overflow-auto border rounded-md">
                  <table className="min-w-full text-sm divide-y divide-gray-200">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-4 py-2 text-left font-medium">#</th>
                        <th className="px-4 py-2 text-left font-medium">
                          Device ID
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {initial.deviceIds!.map((id, index) => (
                        <tr id={`prev_device_row_${id}`} key={index}>
                          <td className="px-4 py-2">{index + 1}</td>
                          <td className="px-4 py-2">{id}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {isDeviceIdFile && deviceIds.length > 0 && (
            <div id="device_ids_section" className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-green-700">
                  {deviceIds.length} device(s) ready to{" "}
                  {isTransfer ? "transfer" : "upload"}
                </p>
                <button
                  id="clear_file_btn"
                  onClick={clearFile}
                  disabled={loading}
                  className="text-red-600 flex items-center gap-1 text-xs hover:text-red-700 disabled:opacity-50"
                >
                  <Icon path={mdiTrashCanOutline} size={0.6} />
                  Clear
                </button>
              </div>
              <div id="device_ids_table" className="max-h-64 overflow-auto border rounded-md">
                <table className="min-w-full text-sm divide-y divide-gray-200">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-4 py-2 text-left font-medium">#</th>
                      <th className="px-4 py-2 text-left font-medium">
                        Device ID
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {deviceIds.map((id, index) => (
                      <tr id={`device_row_${id}`} key={index}>
                        <td className="px-4 py-2">{index + 1}</td>
                        <td className="px-4 py-2">{id}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {isRM && rows.length > 0 && (
            <div id="rm_rows_section" className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-green-700">
                  {rows.length} rows ready to upload
                </p>
                <button
                  id="clear_rm_rows_btn"
                  onClick={clearFile}
                  disabled={loading}
                  className="text-red-600 flex items-center gap-1 text-xs hover:text-red-700 disabled:opacity-50"
                >
                  <Icon path={mdiTrashCanOutline} size={0.6} />
                  Clear
                </button>
              </div>
              <div id="rm_rows_table" className="max-h-64 overflow-auto border rounded-md">
                <table className="min-w-full text-sm divide-y divide-gray-200">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-4 py-2 text-left font-medium">#</th>
                      <th className="px-4 py-2 text-left font-medium">
                        Item Code
                      </th>
                      <th className="px-4 py-2 text-right font-medium">Qty</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {rows.map((row, index) => (
                      <tr id={`rm_row_${row.item_code}_${index}`} key={index}>
                        <td className="px-4 py-2">{index + 1}</td>
                        <td className="px-4 py-2">{row.item_code}</td>
                        <td className="px-4 py-2 text-right">{row.quantity}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {preview && <StockActionPreviewPanel preview={preview} />}

        {error && (
          <p className="text-sm text-red-600 bg-red-50 p-3 rounded-md">
            {error}
          </p>
        )}
      </div>

      <div className="border-t px-6 py-4 flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-end">
        <div className="flex gap-3">
          <button
            id="cancel_btn"
            onClick={() => history.goBack()}
            disabled={loading}
            className="border px-6 py-2 rounded-lg disabled:opacity-50"
          >
            Cancel
          </button>

          {isEdit ? (
            <>
              {file && !(preview && preview.ok) && (
                <button
                  id="preview_btn"
                  onClick={handlePreview}
                  disabled={loading}
                  className={`bg-blue-600 text-white px-6 py-2 rounded-lg transition-all ${
                    loading
                      ? "opacity-50 cursor-not-allowed"
                      : "hover:bg-blue-700"
                  }`}
                >
                  {loading ? "Checking..." : "Preview"}
                </button>
              )}
              <button
                id="update_btn"
                onClick={handleUpdate}
                disabled={loading}
                className={`bg-green-600 text-white px-6 py-2 rounded-lg transition-all ${
                  loading
                    ? "opacity-50 cursor-not-allowed"
                    : "hover:bg-green-700"
                }`}
              >
                {loading ? "Updating..." : "Update & Resubmit"}
              </button>
            </>
          ) : preview && preview.ok ? (
            <button
              id="raise_btn"
              onClick={handleRaise}
              disabled={loading}
              className={`bg-green-600 text-white px-6 py-2 rounded-lg transition-all ${
                loading ? "opacity-50 cursor-not-allowed" : "hover:bg-green-700"
              }`}
            >
              {loading ? "Raising..." : "Raise Request"}
            </button>
          ) : (
            <button
              id="preview_btn"
              onClick={handlePreview}
              disabled={loading}
              className={`bg-blue-600 text-white px-6 py-2 rounded-lg transition-all ${
                loading ? "opacity-50 cursor-not-allowed" : "hover:bg-blue-700"
              }`}
            >
              {loading ? "Checking..." : "Preview"}
            </button>
          )}
        </div>
      </div>

      {loading && <LoadingWidget />}
    </div>
  );
}
